import {PreventionInvalidityCode} from "../preventative-interactions";
import {
    buildMappedCodeGroupFieldKeys,
    categoryIdFromCodeGroupFieldKey,
    parseProductCodesFromCell,
    partitionProductCodeTokens,
} from "../shared/product-code-import-helpers";
import {BaseImportTransformer, toPartNumberUpper} from "./shared";

/**
 * Assigns product codes to parts per mapped code group columns.
 */
export class ProductCodeAssignmentImportTransformer extends BaseImportTransformer {
    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     * @param {{ mappingFields?: Array<{name: string, title: string}>, codeImportSplitter?: string, partnerId?: string|number }} [options]
     */
    async run(mappedData, isFinalRun = false, options = {}) {
        const rows = mappedData ?? [];
        const preventativeIssues = [];
        const mappedCodeGroupFieldKeys = buildMappedCodeGroupFieldKeys(options.mappingFields);
        const splitter = options.codeImportSplitter ?? ' ';
        const partnerId = options.partnerId ?? window.partner?.id;

        /** @type {{index: number, item: *, partNoUpper: string, parsedByFieldKey: Record<string, string[]>, codes: string[], invalidPartsMeta: Record<string, boolean>, invalidCodesMeta: Record<string, { categoryTitle?: string, invalidCodes?: string[] }>}[]} */
        const rowState = rows.map((item, index) => {
            /** @type {Record<string, string[]>} */
            const parsedByFieldKey = {};
            for (const fieldKey of mappedCodeGroupFieldKeys) {
                parsedByFieldKey[fieldKey] = parseProductCodesFromCell(item?.[fieldKey], splitter);
            }
            return {
                index,
                item,
                partNoUpper: toPartNumberUpper(item?.partNumber),
                parsedByFieldKey,
                codes: [],
                invalidPartsMeta: {},
                invalidCodesMeta: {},
            };
        });

        for (const rs of rowState) {
            if (!rs.item) {
                continue;
            }
            if (!rs.partNoUpper) {
                rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
            }
        }

        for (const rs of rowState) {
            if (!rs.item || !rs.partNoUpper) {
                continue;
            }
            const totalCodes = [...mappedCodeGroupFieldKeys].reduce(
                (sum, fieldKey) => sum + (rs.parsedByFieldKey[fieldKey]?.length ?? 0),
                0,
            );
            if (totalCodes === 0) {
                rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
            }
        }

        const uniqueParts = [
            ...new Set(rowState.map((r) => r.partNoUpper).filter(Boolean)),
        ].sort();

        /** @type {Record<string, *>} */
        let partIds = {};
        if (uniqueParts.length > 0) {
            const bundle = await this.ctx.fetchPartInfoByPartNumbers(uniqueParts);
            partIds = bundle.partIds;
        }

        for (const rs of rowState) {
            if (!rs.item) {
                continue;
            }
            if (rs.partNoUpper && !partIds[rs.partNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
                rs.invalidPartsMeta.partNumber = true;
            }
        }

        for (const rs of rowState) {
            if (!rs.item) {
                continue;
            }
            for (const fieldKey of mappedCodeGroupFieldKeys) {
                const categoryId = categoryIdFromCodeGroupFieldKey(fieldKey);
                const tokens = rs.parsedByFieldKey[fieldKey] ?? [];
                if (!tokens.length) {
                    continue;
                }
                const {invalidCodes} = partitionProductCodeTokens(tokens, partnerId);
                if (invalidCodes.length) {
                    rs.codes.push(PreventionInvalidityCode.INVALID_CODE);
                    rs.invalidCodesMeta[fieldKey] = {
                        categoryId,
                        categoryTitle: options.mappingFields?.find((f) => f.name === fieldKey)?.title,
                        invalidCodes,
                    };
                }
            }
        }

        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.MISSING_REQUIRED_FIELDS],
                });
                continue;
            }
            if (!rs.codes.length) {
                continue;
            }
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
                meta: {
                    ...(Object.keys(rs.invalidPartsMeta).length ? {invalidParts: rs.invalidPartsMeta} : {}),
                    ...(Object.keys(rs.invalidCodesMeta).length ? {invalidCodesByGroup: rs.invalidCodesMeta} : {}),
                },
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const apiRequests = rows.map((item, index) => {
            if (!item) {
                return isFinalRun
                    ? null
                    : {
                        partId: null,
                        partNumber: null,
                        codeAssignments: [],
                    };
            }

            const rs = rowState[index];
            const partId = partIds[rs.partNoUpper];

            if (issueIndexSet.has(index)) {
                return isFinalRun
                    ? null
                    : {
                        partId: partId ?? null,
                        partNumber: item.partNumber,
                        codeAssignments: [...mappedCodeGroupFieldKeys].flatMap((fieldKey) => {
                            const categoryId = categoryIdFromCodeGroupFieldKey(fieldKey);
                            const codes = rs.parsedByFieldKey[fieldKey] ?? [];
                            if (!categoryId || !codes.length) {
                                return [];
                            }
                            return [{
                                partCodeCategoryId: Number(categoryId),
                                categoryTitle: options.mappingFields?.find((f) => f.name === fieldKey)?.title,
                                codes,
                            }];
                        }),
                    };
            }

            const codeAssignments = [...mappedCodeGroupFieldKeys].flatMap((fieldKey) => {
                const categoryId = categoryIdFromCodeGroupFieldKey(fieldKey);
                const rawCodes = rs.parsedByFieldKey[fieldKey] ?? [];
                if (!categoryId || !rawCodes.length) {
                    return [];
                }
                const {validCodes} = partitionProductCodeTokens(rawCodes, partnerId);
                if (!validCodes.length) {
                    return [];
                }
                return [{
                    partCodeCategoryId: Number(categoryId),
                    categoryTitle: options.mappingFields?.find((f) => f.name === fieldKey)?.title,
                    codes: validCodes,
                }];
            });

            return {
                partId,
                partNumber: item.partNumber?.trim(),
                codeAssignments,
            };
        });

        return {apiRequests, preventativeIssues};
    }
}
